export interface ValidateConfigurationRequest {
  /**
   * Trigger key as defined in your app's trigger configuration in the app dashboard. For example, `form_submitted` or `invoice_due`.
   * @minLength 1
   * @maxLength 100
   */
  triggerKey: string;
  /**
   * Current selection state of the trigger's filters.
   * @maxSize 5
   */
  selectedFilterOptions?: SelectedFilterOptions[];
  /** Trigger configuration mapping that's saved on the automation. */
  automationConfigMapping?: Record<string, any> | null;
}

export interface SelectedFilterOptions {
  /**
   * Key representing a field from the trigger's [payload schema](https://dev.wix.com/docs/rest/business-management/automations/triggers/the-trigger-payload-schema) that has been configured as an [item selection filter](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/filter-fields#item-selection). For example `formId`.
   * @minLength 1
   * @maxLength 110
   */
  fieldKey?: string;
  /**
   * Values selected for the filter field.
   * @maxSize 100
   */
  values?: string[];
}

export interface ValidateConfigurationResponse {
  /** Whether the trigger configuration is valid. */
  valid?: boolean;
  /** Error details for an invalid trigger configuration. */
  configurationErrors?: ProviderConfigurationError[];
}

export interface ProviderConfigurationError {
  /** Scheme field key. */
  fieldKey?: string | null;
  /** Error message. */
  message?: string;
  /** Label for the call-to-action button displayed with the error. */
  ctaLabel?: string | null;
  /** When the call-to-action button is clicked, the redirect URL. */
  ctaUrl?: string | null;
  /** Error title. */
  title?: string;
}

export interface GetDynamicSchemaRequest {
  /**
   * Trigger key as defined in your app's trigger configuration in the app dashboard. For example, `form_submitted` or `invoice_due`.
   * @minLength 1
   * @maxLength 100
   */
  triggerKey: string;
  /**
   * Current selection state of the trigger's filters.
   * @maxSize 5
   */
  selectedFilterOptions?: SelectedFilterOptions[];
}

export interface GetDynamicSchemaResponse {
  /** A [JSON schema](https://json-schema.org/) corresponding to the filter selection sent in the request. */
  dynamicSchema?: Record<string, any> | null;
}

export interface RefreshPayloadRequest {
  /**
   * Trigger key as defined in your app's trigger configuration in the app dashboard. For example, `form_submitted` or `invoice_due`.
   * @minLength 1
   * @maxLength 100
   */
  triggerKey?: string;
  /**
   * ID of the related resource in GUID format.
   * @format GUID
   */
  externalEntityId?: string | null;
  /** Payload to update. */
  payload: Record<string, any> | null;
}

export interface RefreshPayloadResponse {
  /** Updated payload. */
  payload?: Record<string, any> | null;
  /**
   * Whether the automation activation should be canceled.
   *
   * Default: `false`.
   */
  cancelActivation?: boolean | null;
}

export interface OnAutomationSavedRequest {
  /** Saved automation. */
  automation: Automation;
}

export interface Automation extends AutomationOriginInfoOneOf {
  /** When the origin of the automation is `APPLICATION`, the details of the app that created it. */
  applicationInfo?: ApplicationOrigin;
  /** When the origin of the automation is `PREINSTALLED`, the details of the preinstalled automation. */
  preinstalledInfo?: PreinstalledOrigin;
  /** When the origin of the automation is `TEMPLATE`, the details of the template automation. */
  templateInfo?: TemplateOrigin;
  /**
   * Automation ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number. This increments by 1 whenever the automation is updated. Specify the current revision number whenever updating an existing automation.
   * @readonly
   */
  revision?: string | null;
  /**
   * Who created the automation.
   * @immutable
   * @readonly
   */
  createdBy?: AuditInfo;
  /**
   * When the automation was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Who last updated the automation.
   * @readonly
   */
  updatedBy?: AuditInfo;
  /**
   * When the automation was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Automation name as displayed on the user's site.
   * @minLength 1
   * @maxLength 500
   */
  name?: string;
  /**
   * Automation description.
   * @maxLength 2000
   */
  description?: string | null;
  /** Automation configuration. */
  configuration?: AutomationConfiguration;
  /**
   * How the automation was added to the user's site.
   * @immutable
   */
  origin?: Origin;
  /** Automation settings. Applied only for pre-installed and application automations. Not applied to User origin automations */
  settings?: AutomationSettings;
  /**
   * When the automation is a draft, the draft details.
   * @readonly
   */
  draftInfo?: DraftInfo;
  /**
   * Automation namespace.
   *
   * For internal use only.
   * @internal
   * @maxLength 50
   */
  namespace?: string | null;
  /**
   * Enrichments in use
   * @internal
   * @readonly
   */
  enrichments?: Enrichments;
  /**
   * When the automation's draft was last updated.
   * @internal
   * @readonly
   */
  draftUpdatedDate?: Date | null;
  /**
   * Extended fields.
   *
   * For internal use only.
   * @internal
   */
  extendedFields?: ExtendedFields;
  /**
   * Whether the automation is archived.
   *
   * To archive an automation, set this to `true`. To restore an archived automation, set this to `false`.
   */
  archived?: boolean;
  /** Auto archive policy */
  autoArchivePolicy?: AutoArchivePolicy;
  /**
   * For internal use only. Notes related to the automation.
   * @internal
   */
  notes?: Notes;
  /**
   * Indicates if this automation is used as a base automation for preinstalls
   * For internal use only.
   * @internal
   */
  baseAutomation?: boolean | null;
}

/** @oneof */
export interface AutomationOriginInfoOneOf {
  /** When the origin of the automation is `APPLICATION`, the details of the app that created it. */
  applicationInfo?: ApplicationOrigin;
  /** When the origin of the automation is `PREINSTALLED`, the details of the preinstalled automation. */
  preinstalledInfo?: PreinstalledOrigin;
  /** When the origin of the automation is `TEMPLATE`, the details of the template automation. */
  templateInfo?: TemplateOrigin;
}

export interface AIMetadata {
  /**
   * Origin agent identifier.
   * @minLength 1
   * @maxLength 64
   */
  originAgentIdentifier?: string | null;
}

export interface ActionSettings {
  /**
   * IDs of actions whose `skipActionExpression` can't be modified.
   *
   * An action's `skipActionExpression` determines whether the action is skipped. Including an action's ID in this array makes its `skipActionExpression` permanent, so it can't be changed.
   *
   * When empty, the `skipActionExpression` of all actions can be edited.
   * @maxSize 30
   * @format GUID
   */
  permanentActionIds?: string[];
  /**
   * IDs of read-only actions. Read-only actions cannot be modified.
   *
   * When empty and the automation's `settings.readonly` is `false`, all actions are modifiable.
   * @maxSize 30
   * @format GUID
   */
  readonlyActionIds?: string[];
  /**
   * Whether to disable the ability to add a delay to the automation.
   *
   * Default: `false`.
   * @internal
   */
  disableDelayAddition?: boolean;
  /**
   * Whether to disable the ability to add a condition to the automation.
   *
   * Default: `false`.
   * @internal
   */
  disableConditionAddition?: boolean;
}

export enum Domain {
  /** User domain (default). */
  USER = 'USER',
  /** Wix domain. */
  WIX = 'WIX',
  /** Wix account-level domain. */
  WIX_ACCOUNT = 'WIX_ACCOUNT',
}

export interface TriggerSettings {
  /**
   * Whether to disable the ability to modify the trigger configuration. When `true`, users can't modify the settings in `configuration.trigger`.
   *
   * Default: `true` for automations whose `origin` is `PREINSTALLED` or `APPLICATION`.
   * @internal
   */
  disableConfigurationModification?: boolean;
}

export interface Enrichment {
  /**
   * Enrichment input mappings.
   * @maxSize 2
   */
  inputMappings?: Record<string, any>[] | null;
}

export interface AuditInfo extends AuditInfoIdOneOf {
  /**
   * [User ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#user-id).
   * @format GUID
   */
  userId?: string;
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
  /**
   * @internal
   * @readonly
   */
  aiMetadata?: AIMetadata;
}

/** @oneof */
export interface AuditInfoIdOneOf {
  /**
   * [User ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#user-id).
   * @format GUID
   */
  userId?: string;
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
}

export interface AutomationConfiguration {
  /** Status of the automation on the site. */
  status?: Status;
  /** Trigger configuration. */
  trigger?: Trigger;
  /**
   * Root action IDs. A root action is the first action that runs after the trigger occurs. Root actions run in parallel.
   *
   * > **Note**: You can currently only specify 1 root action.
   * @maxSize 20
   * @format GUID
   */
  rootActionIds?: string[];
  /** Actions the automation can execute, as `key:value` pairs. For the key, specify the action ID. The value must be an object structured as described below. */
  actions?: Record<string, Action>;
  /**
   * App-defined actions included in this automation.
   * @internal
   * @readonly
   */
  filterableAppDefinedActions?: FilterableAppDefinedActions;
}

export enum TimeUnit {
  /** Minutes. */
  MINUTES = 'MINUTES',
  /** Hours. */
  HOURS = 'HOURS',
  /** Days. */
  DAYS = 'DAYS',
  /** Weeks. */
  WEEKS = 'WEEKS',
  /** Months. */
  MONTHS = 'MONTHS',
}

export interface Filter {
  /**
   * Filter ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Field key.
   *
   * You can find the field key in [the trigger payload schema](https://dev.wix.com/docs/rest/business-management/automations/triggers/the-trigger-payload-schema).
   * @minLength 1
   * @maxLength 110
   */
  fieldKey?: string;
  /**
   * Filter expression in bracket notation.
   *
   * For the automation to run, the expression must evaluate to `true`.
   * @maxLength 5000
   */
  filterExpression?: string;
}

export interface FutureDateActivationOffset {
  /**
   * Amount of time before the trigger to run the automation.
   *
   * > **Note**: To delay an automation after the trigger event occurs, use a [delay action](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#delay-action).
   * @maxLength 1000
   */
  preScheduledEventOffsetExpression?: string;
  /** Unit in which to set the action offset. */
  scheduledEventOffsetTimeUnit?: TimeUnit;
}

export interface RateLimit {
  /**
   * Maximum number of times the trigger can be activated.
   * @maxLength 1000
   */
  maxActivationsExpression?: string;
  /**
   * Duration of the rate limit. The rate limit applies for the specified duration and then expires.
   *
   * When empty, the rate limit does not expire.
   * @maxLength 1000
   */
  durationExpression?: string | null;
  /** Unit in which to set the duration of the rate limit. */
  durationTimeUnit?: TimeUnit;
  /**
   * Activation identifier used to count the number of activations.
   * @minLength 1
   * @maxLength 400
   */
  uniqueIdentifierExpression?: string | null;
}

export interface FilterValueSelection {
  /**
   * Values that can help the user filter certain automations. Specify values in the following format: `<filter_id>__<selected_value>`.
   * @maxLength 80
   * @maxSize 50
   */
  selectedFilterValues?: string[];
}

export interface ConditionExpressionGroup {
  /** Logical operator used to evaluate the condition expressions. */
  operator?: Operator;
  /**
   * Expressions evaluated using the selected operator.
   * @minSize 1
   * @maxSize 20
   * @maxLength 5000
   */
  booleanExpressions?: string[];
}

export enum Operator {
  /** `OR` operator. */
  OR = 'OR',
  /** `AND` operator. */
  AND = 'AND',
}

export interface CodeSnippet {
  /** Logical operator used to evaluate the condition expressions. */
  language?: Language;
  /**
   * Expressions evaluated using the selected operator. this code should comply the language syntax. and format
   * @maxLength 3000
   */
  code?: string;
}

export enum Language {
  /** JavaScript. */
  JAVASCRIPT = 'JAVASCRIPT',
}

/** Path definition */
export interface Path {
  /**
   * Unique path ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Path name.
   * @minLength 1
   * @maxLength 100
   */
  name?: string;
  /**
   * ID of the action to run when this path is taken.
   * @format GUID
   */
  postActionId?: string | null;
  /**
   * Editable display name for the path.
   * @minLength 1
   * @maxLength 60
   */
  displayName?: string | null;
}

export enum Type {
  /** Defined by a [Wix app](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#app-defined-action). This action type is available in the site dashboard. */
  APP_DEFINED = 'APP_DEFINED',
  /**
   * Condition action. Evaluates a condition to determine which action runs next.
   *
   * Learn more about [adding and setting up conditions](https://support.wix.com/en/article/the-new-automation-builder-understanding-conditions-operators) in Wix Automations.
   *
   * > **Note**: When `skipActionExpression` evaluates to `true`, the actions in `conditionInfo.truePostActionIds` run and those in `conditionInfo.falsePostActionIds` are skipped.
   */
  CONDITION = 'CONDITION',
  /**
   * Code condition action. Evaluates custom code to determine which action runs next.
   *
   * Learn more about [adding and setting up conditions](https://support.wix.com/en/article/the-new-automation-builder-understanding-conditions-operators) in Wix Automations.
   *
   * > **Note**: When `skipActionExpression` evaluates to `true`, the actions in `codeConditionInfo.truePostActionIds` run and those in `codeConditionInfo.falsePostActionIds` are skipped.
   */
  CODE_CONDITION = 'CODE_CONDITION',
  /** Delay. The automation must wait before executing the next step. */
  DELAY = 'DELAY',
  /** Rate-limiter. This stops the automation flow if the subsequent action has reached its maximum allowed executions. */
  RATE_LIMIT = 'RATE_LIMIT',
  /**
   * Set variables.
   * @documentationMaturity preview
   * @internal
   */
  SET_VARIABLES = 'SET_VARIABLES',
  /**
   * Output.
   *
   * This action type does not support `skipActionExpression`.
   * @documentationMaturity preview
   * @internal
   */
  OUTPUT = 'OUTPUT',
  /**
   * Split.
   *
   * This action type does not support `skipActionExpression`.
   * @documentationMaturity preview
   * @internal
   */
  SPLIT = 'SPLIT',
  /**
   * Merge. XOR-join for condition branches — fires on the one arriving path.
   *
   * This action type does not support `skipActionExpression`.
   * @documentationMaturity preview
   * @internal
   */
  MERGE = 'MERGE',
}

export interface AppDefinedAction {
  /**
   * ID of the app that defines the action.
   * @format GUID
   */
  appId?: string;
  /**
   * Action key.
   * @minLength 1
   * @maxLength 100
   */
  actionKey?: string;
  /**
   * Action input mapping as defined in the action's [input schema](https://dev.wix.com/docs/rest/business-management/automations/actions/the-action-input-schema).
   *
   * The input mapping must include all parameters specified in the action's input schema's `required` array, and can include any other parameters from the schema.
   */
  inputMapping?: Record<string, any> | null;
  /**
   * Action skip conditions. An action is skipped if any of the specified conditions evaluates to `true`.
   *
   * > **Note**: Actions that follow a skipped action still run.
   * @maxSize 10
   * @deprecated Action skip conditions. An action is skipped if any of the specified conditions evaluates to `true`.
   *
   * > **Note**: Actions that follow a skipped action still run.
   * @replacedBy skip_action_expression
   * @targetRemovalDate 2026-02-01
   */
  skipConditionOrExpressionGroups?: ConditionExpressionGroup[];
  /**
   * IDs of actions that run in parallel after the action completes.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
  /** Action output schema. When specified, this replaces the action schema. */
  overrideOutputSchema?: Record<string, any> | null;
}

export interface ConditionAction {
  /**
   * Condition is `true` if one or more of the expression groups evaluates to `true`.
   * @minSize 1
   * @maxSize 10
   */
  orExpressionGroups?: ConditionExpressionGroup[];
  /**
   * IDs of actions to run when the condition evaluates to `true`.
   * @maxSize 1
   * @format GUID
   */
  truePostActionIds?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `false`.
   * @maxSize 1
   * @format GUID
   */
  falsePostActionIds?: string[];
  /**
   * ID of the MERGE action for this condition. When absent, branches run independently (current behavior).
   * @format GUID
   */
  mergeActionId?: string | null;
}

export interface CodeConditionAction {
  /** condition code. This is a code that can be used to identify the condition in the automation. code should return a boolean value. otherwise the value will be tried to be casted to boolean. */
  snippet?: CodeSnippet;
  /**
   * dynamic variables used in the code snippet. these variables can be used inside the code using var('variableName') function.
   * this list is mainly for validation purpose.
   * @maxSize 50
   * @maxLength 100
   */
  dynamicVariableExpressions?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `true`.
   * @maxSize 1
   * @format GUID
   */
  truePostActionIds?: string[];
  /**
   * IDs of actions to run when the condition evaluates to `false`.
   * @maxSize 1
   * @format GUID
   */
  falsePostActionIds?: string[];
  /**
   * ID of the MERGE action for this condition. When absent, branches run independently (current behavior).
   * @format GUID
   */
  mergeActionId?: string | null;
}

export interface DelayAction {
  /**
   * Time to wait before running the action. The wait time starts from when the current action completes.
   *
   * > **Note**: To define the wait time from a specific moment in time, use `dueDateExpression`.
   * @maxLength 1000
   */
  offsetExpression?: string | null;
  /** Unit in which to set the wait time to wait before the action runs. */
  offsetTimeUnit?: TimeUnit;
  /**
   * Action run date in milliseconds elapsed since January 1, 1970 UTC.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @maxLength 1000
   * @deprecated Action run date in milliseconds elapsed since January 1, 1970 UTC.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @replacedBy due_date_expression
   * @targetRemovalDate 2026-02-01
   */
  dueDateEpochExpression?: string | null;
  /**
   * Action run date as a timestamp or DateTime expression using bracket notation.
   *
   * > **Note**: If an `offsetExpression` is defined, the delay is calculated from the time of the offset.
   * @maxLength 1000
   */
  dueDateExpression?: string | null;
  /**
   * IDs of actions to run in parallel after the time delay.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface RateLimitAction {
  /**
   * Maximum number of times the action can run.
   * @maxLength 1000
   */
  maxActivationsExpression?: string;
  /**
   * Rate limit duration.
   *
   * When empty, the rate limit does not expire.
   * @maxLength 1000
   */
  rateLimitDurationExpression?: string | null;
  /** Unit in which to set the duration of the rate limit. */
  rateLimitDurationTimeUnit?: TimeUnit;
  /**
   * Unique identifier of each activation by which rate limiter counts activations.
   * @minLength 1
   * @maxLength 400
   */
  uniqueIdentifierExpression?: string | null;
  /**
   * IDs of actions to run in parallel after the action completes.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface SetVariablesAction {
  /** Output mapping. For example: `{"someField": "{{10}}", "someOtherField": "{{multiply( var('account.points.balance') ;2 )}}" }`. */
  outputMapping?: Record<string, any> | null;
  /**
   * Output JSON schema representation.
   *
   * > **Note**: To minimize request size, you can also specify a string.
   */
  outputSchema?: Record<string, any> | null;
  /**
   * IDs of actions to run in parallel after variable initialization.
   * @maxSize 1
   * @format GUID
   */
  postActionIds?: string[];
}

export interface OutputAction {
  /** Output action output mapping. */
  outputMapping?: Record<string, any> | null;
}

export interface SplitAction {
  /**
   * List of paths to split execution into.
   * @minSize 2
   * @maxSize 10
   */
  paths?: Path[];
}

export interface MergeAction {
  /**
   * ID of the action to execute after the arriving branch completes.
   * @format GUID
   */
  postActionId?: string | null;
}

export enum Status {
  /** Active. Active automations can be triggered. */
  ACTIVE = 'ACTIVE',
  /** Inactive. Inactive automations cannot be triggered. */
  INACTIVE = 'INACTIVE',
}

export interface Trigger {
  /**
   * ID of the app that defines the trigger.
   * @format GUID
   */
  appId?: string;
  /**
   * Trigger key.
   *
   * Learn about [setting up a trigger](https://dev.wix.com/docs/rest/business-management/automations/triggers/add-a-trigger-to-your-app#step-1--set-up-the-trigger).
   * @minLength 1
   * @maxLength 100
   */
  triggerKey?: string;
  /**
   * Schema field filters. All filter conditions must be met for the automation to run.
   *
   * Learn more about setting up [automation filters](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#filters).
   * @maxSize 5
   */
  filters?: Filter[];
  /**
   * Automation offset. You can schedule automations to run before the trigger occurs.
   *
   * Learn more about [scheduled events](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#scheduled-events).
   */
  scheduledEventOffset?: FutureDateActivationOffset;
  /**
   * Limit on the number of times an automation can be triggered.
   * @internal
   */
  rateLimit?: RateLimit;
  /**
   * Optional configuration of a schema that is optionally offered by the trigger provider to affect the behavior of the trigger.
   *
   * For example, a trigger provider can offer a schema that allows the user to configure the trigger to occur at a certain time of day by adding a `startDate` field to the [trigger payload schema](https://dev.wix.com/docs/rest/business-management/automations/triggers/the-trigger-payload-schema). The user can use this parameter to specify a start date for the automation.
   * @internal
   */
  automationConfigMapping?: Record<string, any> | null;
  /** Trigger schema override. When specified, this replaces the trigger schema. */
  overrideSchema?: Record<string, any> | null;
  /**
   * Selected filter values by which the user can filter certain automations.
   *
   * For example, the user can filter relevant automations that run for a specific formId when formId is filtered.
   * `selected_filter_values` will hold a list of strings in the format <filterId>__<userSelectedValue>.
   * The filterId should be extracted from the trigger in the dev center. This list will hold values from all filters.
   * @internal
   * @readonly
   */
  filterValueSelection?: FilterValueSelection;
}

export interface Action extends ActionInfoOneOf {
  /** Details of the action whose `type` is `APP_DEFINED`. */
  appDefinedInfo?: AppDefinedAction;
  /** Details of the action whose `type` is `CONDITION`. */
  conditionInfo?: ConditionAction;
  /** Details of the action whose `type` is `CODE_CONDITION`. */
  codeConditionInfo?: CodeConditionAction;
  /** Details of the action whose `type` is `DELAY`. */
  delayInfo?: DelayAction;
  /** Details of the action whose `type` is `RATE_LIMIT`. */
  rateLimitInfo?: RateLimitAction;
  /**
   * Details of the action whose `type` is `SET_VARIABLES`.
   * @internal
   */
  setVariablesInfo?: SetVariablesAction;
  /**
   * Details of the action whose `type` is `OUTPUT`.
   * @internal
   */
  outputInfo?: OutputAction;
  /**
   * Details of the action whose `type` is `SPLIT`.
   * @internal
   */
  splitInfo?: SplitAction;
  /**
   * Details of the action whose `type` is `MERGE`.
   * @internal
   */
  mergeInfo?: MergeAction;
  /**
   * Action ID.
   *
   * Unique identifier for the action instance in the current automation. Use this to refer to this action in the automation flow.
   *
   * If not specified, automatically generated by Wix.
   * @format GUID
   */
  _id?: string | null;
  /** [Action type](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#action-type). */
  type?: Type;
  /**
   * Editable display name for the action.
   * @minLength 1
   * @maxLength 60
   */
  displayName?: string | null;
  /**
   * [Action namespace](https://dev.wix.com/docs/rest/business-management/automations/automations/automations-v2/configure-your-automation#namespace). This differentiates it from other actions of the same type.
   *
   * If the action outputs any data, the data appears under the namespace in the payload sent to the subsequent steps in the automation. If the user has multiple actions with the same `appId` and `actionKey`, the output of the previous action is overwritten.
   * @minLength 1
   * @maxLength 100
   */
  namespace?: string | null;
  /**
   * Evaluated to determine whether the action is skipped. When evaluated to `true`, the action is skipped. Otherwise, the action runs.
   *
   * When empty, the action runs.
   * @maxLength 1000
   */
  skipActionExpression?: string | null;
}

/** @oneof */
export interface ActionInfoOneOf {
  /** Details of the action whose `type` is `APP_DEFINED`. */
  appDefinedInfo?: AppDefinedAction;
  /** Details of the action whose `type` is `CONDITION`. */
  conditionInfo?: ConditionAction;
  /** Details of the action whose `type` is `CODE_CONDITION`. */
  codeConditionInfo?: CodeConditionAction;
  /** Details of the action whose `type` is `DELAY`. */
  delayInfo?: DelayAction;
  /** Details of the action whose `type` is `RATE_LIMIT`. */
  rateLimitInfo?: RateLimitAction;
  /**
   * Details of the action whose `type` is `SET_VARIABLES`.
   * @internal
   */
  setVariablesInfo?: SetVariablesAction;
  /**
   * Details of the action whose `type` is `OUTPUT`.
   * @internal
   */
  outputInfo?: OutputAction;
  /**
   * Details of the action whose `type` is `SPLIT`.
   * @internal
   */
  splitInfo?: SplitAction;
  /**
   * Details of the action whose `type` is `MERGE`.
   * @internal
   */
  mergeInfo?: MergeAction;
}

export interface FilterableAppDefinedActions {
  /**
   * App-defined action identifiers. Identifiers have the following format: `${appId}_${actionKey}`.
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 150
   */
  actionIdentifiers?: string[];
}

export enum Origin {
  /** Created by a [Wix user](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#wix-user). */
  USER = 'USER',
  /** Created by a [Wix app](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#wix-app) for a particular site. */
  APPLICATION = 'APPLICATION',
  /** [Preinstalled automation](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/pre-installed-automations/about-pre-installed-automations). */
  PREINSTALLED = 'PREINSTALLED',
  /**
   * For internal use only.
   * @internal
   */
  RECIPE = 'RECIPE',
  /**
   * For internal use only.
   * @internal
   */
  TEMPLATE = 'TEMPLATE',
}

export interface ApplicationOrigin {
  /**
   * [App ID](https://dev.wix.com/docs/build-apps/get-started/overview/glossary#app-id).
   *
   * You can find the app ID on the [Home page](https://manage.wix.com/app-selector?title=Select+an+App&primaryButtonText=Select+Site&actionUrl=https%3A%2F%2Fdev.wix.com%2Fapps%2F%7BappId%7D%home) in the app's dashboard.
   * @format GUID
   */
  appId?: string;
  /**
   * For internal use only.
   * @internal
   * @format GUID
   */
  appDefinedExternalId?: string | null;
}

export interface PreinstalledOrigin {
  /**
   * ID of the app that added the [preinstalled automation](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/pre-installed-automations/about-pre-installed-automations).
   * @format GUID
   */
  appId?: string;
  /**
   * For internal use only.
   * @internal
   * @format GUID
   */
  componentId?: string;
  /**
   * For internal use only.
   * @internal
   */
  componentVersion?: number;
  /**
   * Whether the automation is an override.
   *
   * When a user modifies the preinstalled automation installed on their site, a site-specific version of the automation is created that overrides the automation that was originally installed. This override automation is assigned a **new** automation ID — it does not reuse the ID of the original preinstalled automation.
   *
   * > **Note**: Once an override exists, [Get Automation](https://dev.wix.com/docs/api-reference/business-management/automations/automations/automations-v2/get-automation) on the *original* preinstalled automation's ID returns `404 Not Found`. Use [Query Automations](https://dev.wix.com/docs/api-reference/business-management/automations/automations/automations-v2/query-automations) (for example, by `configuration.trigger.triggerKey`) to find the current automation, or retrieve `automationId` from the activation log.
   * >
   * > An override automation can no longer be updated by the app that originally installed it.
   * >
   * > To revert to the original preinstalled version, the user must delete the override by calling the Delete Automation method. Calling Delete Automation on an override automation removes the override and restores the preinstalled automation that was previously installed, once again accessible via its original ID.
   *
   * Default: `false`.
   * @immutable
   * @readonly
   */
  override?: boolean | null;
}

export interface TemplateOrigin {
  /**
   * ID of the app that added the template automation.
   * @format GUID
   */
  appId?: string;
  /**
   * App component ID.
   *
   * For internal use only.
   * @internal
   * @format GUID
   */
  componentId?: string;
  /**
   * App component version.
   *
   * For internal use only.
   * @internal
   */
  componentVersion?: number;
}

export interface AutomationSettings {
  /**
   * Whether the automation is hidden from users.
   *
   * Default: `false`
   */
  hidden?: boolean;
  /**
   * Whether the automation is read-only.
   *
   * When `true`, site owners can't modify the automation or any of its actions. When `false`, users can configure specific actions as skippable or read-only using `settings.actionSettings`.
   *
   * Default: `false`.
   *
   * > **Note**: Setting `readOnly` to `true` overrides `settings.actionSettings`.
   */
  readonly?: boolean;
  /**
   * Whether to disable the option to delete the automation from the site.
   *
   * Default: `false`.
   */
  disableDelete?: boolean;
  /**
   * Whether to disable the option to change the automation's `configuration.status`.
   *
   * Default: `false`.
   */
  disableStatusChange?: boolean;
  /** Automation action settings. */
  actionSettings?: ActionSettings;
  /**
   * Domain type.
   * @internal
   * @immutable
   */
  domain?: Domain;
  /**
   * Automation trigger settings.
   * @internal
   */
  triggerSettings?: TriggerSettings;
}

export interface DraftInfo {
  /**
   * ID of the original automation.
   * @format GUID
   * @readonly
   */
  originalAutomationId?: string | null;
}

export interface Enrichments {
  /** Whether the studio site enrichment is wanted. */
  studioSite?: Enrichment;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface AutoArchivePolicy {
  /**
   * Date when to archive the automation
   * If this date in the past, nothing will happen (automation will not go into archived state)
   * If this date in the future, on this date the automation will be updated with archived = true and configuration.status = INACTIVE
   * After this date the automation may be unarchived and archived again, this date will have no influence
   */
  archiveDate?: Date | null;
}

export interface Notes {
  /**
   * Notes associated with the automation.
   * @internal
   * @maxSize 5
   */
  notes?: Note[];
}

export interface Note {
  /**
   * Note content.
   * @internal
   * @maxLength 400
   */
  content?: string;
  /**
   * Note position for UI rendering.
   * @internal
   */
  position?: Position;
  /**
   * Dimensions of the note.
   * @internal
   */
  dimensions?: Dimensions;
  /**
   * Color of the note
   * @internal
   * @format COLOR_HEX
   */
  color?: string | null;
}

export interface Position {
  /**
   * offset from canvas center in pixels.
   * @internal
   */
  offset?: Offset;
}

export interface Offset {
  /**
   * X coordinate of the starting point.
   * @internal
   */
  x?: number;
  /**
   * Y coordinate of the starting point.
   * @internal
   */
  y?: number;
}

export interface Dimensions {
  /**
   * Width of the note in pixels.
   * @internal
   * @min 30
   */
  widthInPx?: number;
  /**
   * Height of the note in pixels.
   * @internal
   * @min 30
   */
  heightInPx?: number;
}

export interface OnAutomationSavedResponse {}

export interface OnAutomationDeletedRequest {
  /**
   * ID of the deleted automation.
   * @format GUID
   */
  automationId: string;
}

export interface OnAutomationDeletedResponse {}

export interface TriggerProviderSPIConfig {
  /**
   * The trigger configuration
   *
   * example of a trigger:
   *
   * {
   * appId: "56cc7843-fdfc-417f-a660-d4af9a2eafe3",
   * triggerKey: "form_submit",
   * displayName: "Visitor submits a form",
   * payloadDataSchema: {
   * "$schema": "http://json-schema.org/draft-07/schema",
   * "type": "object",
   * "title": "Visitor submits a form Payload Schema",
   * "description": "The schema of the payload that is part of the triggered event",
   * "default": {},
   * "examples": [
   * {
   * "formId": "60d2cd50-1047-4164-884d-8c24fbda84bb",
   * "formName": "My Cool Form
   * }
   * ],
   * "properties": {
   * "formId": {
   * "$id": "#/properties/formId",
   * "type": "string",
   * "format": "uuid",
   * "title": "Form ID",
   * "description": "The form identifier",
   * "default": "",
   * "examples": [
   * "60d2cd50-1047-4164-884d-8c24fbda84bb"
   * ]
   * },
   * "formName": {
   * "$id": "#/properties/formName",
   * "type": "string",
   * "title": "Form Name",
   * "description": "The form display name",
   * "default": "",
   * "examples": [
   * "My Form"
   * ]
   * },
   * "contactId": {
   * "$id": "#/properties/contactId",
   * "type": "string",
   * "format": "uuid",
   * "title": "Contact Id",
   * "description": "The Id of the contact which submitted the form",
   * "default": "",
   * "identityType": "contact", // can be contact, visitor, user limited to 1 type per identity,
   * "namespace": "contact" // friendly name to be used later on, in order to user that person's data "contact.firstName"
   * }
   * },
   * "required": [
   * "formId",
   * "formName",
   * "contactId"
   * ],
   * "additionalProperties": false
   * }
   * }
   */
  triggerConfig?: Record<string, any> | null;
  /** URL to the trigger provider service */
  baseUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Extension ID in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionId?: string | null;
  /**
   * Extension type in Dev Center.
   * @internal
   * @maxLength 256
   */
  appExtensionType?: string | null;
  /**
   * Invoked function.
   * @internal
   * @maxLength 256
   */
  functionName?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
